import { useEffect, useState, type Dispatch, type SetStateAction } from 'react'
import {
  Logger,
  type LocationAutocompleteResult,
  type LatLng,
  type SmallAddressWithLocationId,
  type LocationExchangeInput,
  type LocationExchangeSuccess,
  type LocationExchangeUnit,
} from '@terros/common'
import { useServerRequest } from '../api'
import { useAutocomplete } from './useAutocomplete'

const logger = new Logger('usePlacesAutocomplete')

type Params = {
  search: string
  setSearch: Dispatch<SetStateAction<string>>
  bias?: LatLng
  version?: number
}

export type PlaceDetailsWithUnits = {
  details: SmallAddressWithLocationId
  /** the units at the address when the address has no unit of its own; empty when we have none */
  units: LocationExchangeUnit[]
}

type Output = {
  data: LocationAutocompleteResult[]
  getDetails: (place_id: string) => Promise<SmallAddressWithLocationId>
  getDetailsWithUnits: (placeId: string) => Promise<PlaceDetailsWithUnits>
  isLoading: boolean
}

export const useAutocompleteBox = ({ search, setSearch, bias, version = 3 }: Params): Output => {
  const serverRequest = useServerRequest<LocationExchangeInput, LocationExchangeSuccess>('/location/exchange')
  const { data: autoCompleteData, error, isFetching } = useAutocomplete({ query: search, bias })
  const [prevData, setPrevData] = useState<LocationAutocompleteResult[]>([])

  useEffect(() => {
    if (autoCompleteData !== undefined) setPrevData(autoCompleteData)
  }, [autoCompleteData])

  const data = autoCompleteData ?? prevData

  useEffect(() => {
    if (error) {
      logger.error('Error fetching places:', error)
    }
  }, [error])

  const getDetails = async (placeId: string): Promise<SmallAddressWithLocationId> => {
    setSearch('')
    const res = await serverRequest({ placeId, version })
    return res.details
  }

  const getDetailsWithUnits = async (placeId: string): Promise<PlaceDetailsWithUnits> => {
    setSearch('')
    const res = await serverRequest({ placeId, version, includeUnits: true })
    return { details: res.details, units: res.units ?? [] }
  }

  return { data, getDetails, getDetailsWithUnits, isLoading: isFetching }
}
